/**
 * Voice Activity Detection (VAD) baseado em Web Audio API + AnalyserNode (RMS).
 *
 * Estratégia:
 *  - getUserMedia → MediaStream → AudioContext → AnalyserNode
 *  - A cada 50ms calcula RMS (root-mean-square) do buffer de tempo
 *  - Estados: silent → speaking (após N frames acima do threshold)
 *  - speaking → silent (após M ms de RMS abaixo do threshold)
 *
 * Sem dependências externas. Funciona em todos os navegadores modernos.
 */
export interface VADOptions {
  /** Threshold de RMS (0-1) para considerar como fala. Default: 0.015 */
  rmsThreshold?: number;
  /** Frames consecutivos acima do threshold para iniciar fala. Default: 3 (~150ms) */
  speechFramesToStart?: number;
  /** Tempo de silêncio (ms) para considerar fim de fala. Default: 700ms */
  silenceMsToStop?: number;
  /** Intervalo de análise (ms). Default: 50ms */
  pollIntervalMs?: number;
  /** Callback quando fala inicia */
  onSpeechStart?: () => void;
  /** Callback quando fala termina (com áudio capturado) */
  onSpeechEnd?: (audioBlob: Blob, durationMs: number) => void;
  /** Callback de nível RMS contínuo (para visualização) */
  onLevel?: (rms: number) => void;
}

export interface VADController {
  start: () => Promise<void>;
  stop: () => void;
  isActive: () => boolean;
  isSpeaking: () => boolean;
}

export function createVAD(options: VADOptions = {}): VADController {
  const {
    rmsThreshold = 0.010,
    speechFramesToStart = 2,
    silenceMsToStop = 600,
    pollIntervalMs = 50,
    onSpeechStart,
    onSpeechEnd,
    onLevel,
  } = options;
  // Minimum captured speech length to be considered intentional (filters out clicks/short noises).
  const minSpeechMs = 350;

  let audioContext: AudioContext | null = null;
  let stream: MediaStream | null = null;
  let analyser: AnalyserNode | null = null;
  let recorder: MediaRecorder | null = null;
  let pollTimer: number | null = null;

  let active = false;
  let speaking = false;
  let consecutiveSpeechFrames = 0;
  let lastSpeechAt = 0;
  let speechStartedAt = 0;
  let chunks: Blob[] = [];

  const computeRMS = (buf: Float32Array): number => {
    let sum = 0;
    for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
    return Math.sqrt(sum / buf.length);
  };

  const tick = () => {
    if (!analyser || !active) return;
    const buf = new Float32Array(analyser.fftSize);
    analyser.getFloatTimeDomainData(buf);
    const rms = computeRMS(buf);
    onLevel?.(rms);

    const now = performance.now();
    if (rms > rmsThreshold) {
      consecutiveSpeechFrames++;
      lastSpeechAt = now;
      if (!speaking && consecutiveSpeechFrames >= speechFramesToStart) {
        speaking = true;
        speechStartedAt = now;
        chunks = [];
        recorder?.start(100);
        onSpeechStart?.();
      }
    } else {
      consecutiveSpeechFrames = 0;
      if (speaking && now - lastSpeechAt > silenceMsToStop) {
        speaking = false;
        const duration = Math.round(now - speechStartedAt);
        recorder?.stop();
        // onSpeechEnd is fired from recorder.onstop with the assembled blob
        speechStartedAt = 0;
        // store duration so onstop can read it
        (recorder as any).__lastDurationMs = duration;
      }
    }
  };

  return {
    async start() {
      if (active) return;
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const source = audioContext.createMediaStreamSource(stream);
      analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);

      // MIME type fallback
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4";

      recorder = new MediaRecorder(stream, { mimeType: mime });
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mime });
        chunks = [];
        const duration = (recorder as any).__lastDurationMs ?? 0;
        // Drop blobs that are clearly too short to carry intent — avoids garbage transcripts.
        if (blob.size > 0 && duration >= minSpeechMs) onSpeechEnd?.(blob, duration);
      };

      active = true;
      pollTimer = window.setInterval(tick, pollIntervalMs);
    },
    stop() {
      active = false;
      speaking = false;
      consecutiveSpeechFrames = 0;
      if (pollTimer !== null) {
        clearInterval(pollTimer);
        pollTimer = null;
      }
      try { recorder?.state !== "inactive" && recorder?.stop(); } catch { /* noop */ }
      recorder = null;
      stream?.getTracks().forEach((t) => t.stop());
      stream = null;
      audioContext?.close();
      audioContext = null;
      analyser = null;
    },
    isActive: () => active,
    isSpeaking: () => speaking,
  };
}

/**
 * Detecta wake-word em um texto transcrito.
 * Match flexível: case-insensitive, ignora acentos básicos, permite "pequena" variação.
 */
export function detectWakeWord(text: string, wakeWords: string[]): string | null {
  const norm = (s: string) =>
    s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9 ]/g, " ").trim();
  const t = norm(text);
  for (const w of wakeWords) {
    const nw = norm(w);
    if (!nw) continue;
    // match como palavra inteira, no início ou após espaço
    const re = new RegExp(`(^|\\s)${nw.replace(/\s+/g, "\\s+")}(\\s|$|[,.!?])`);
    if (re.test(t)) return w;
  }
  return null;
}
